// "Pulled away" cover (Console V9, 2026-10-28): when someone answers an emergency, their work due
// within the hour moves at once to the people on shift who may do it. Back end:
// src/aithora/v3/worklist/cover-routes.ts (the routes) and ./cover.ts (the rules).
//
//   GET  /worklist/cover                       mine: what was moved to me (Accept), and what of mine
//                                              was moved while I was pulled away (Take back)
//   POST /worklist/cover/accept {itemRefs}     Accept (one or all): the item is mine; the ladder's clock stops
//   POST /worklist/cover/pass {itemRef}        "Can't, pass on": to the next eligible person
//   POST /worklist/cover/take-back {causeId, itemRef?}  take back what is not done yet (or one item)
//   GET  /facilities/:id/worklist/coverage     "covered on time" (30 days) and the live cover per
//                                              emergency, for the Facility Admin and the Aithora team
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

/** One moved item, as the Worklist names it (a task, or a dose on the med pass). */
export interface CoverItemRef {
  tab: 'tasks' | 'medpass';
  sourceId: string;
  /** The day (YYYY-MM-DD) of a repeating item, null for a one-off. */
  occurrence: string | null;
}

/** Moved to me. `words` is the server's line, "Covering for Jackie · Edna's emergency". */
export interface CoverReceived {
  itemRef: CoverItemRef;
  /** The emergency (or Guardian case) that pulled the person away. */
  causeId: string;
  words: string;
  dueAt: string | null;
  /** Minutes either side of the due time it may still be given on time (30 for a time-critical dose). */
  windowMin: number | null;
  /** I took it (Accept): the pass-on clock has stopped. */
  accepted: boolean;
  /** When it last moved: the pass-on clock (5 minutes, 2 for time-critical) counts from here. */
  movedAt?: string;
  /** The task's name or the medicine, and the resident. */
  task?: string | null;
  residentName?: string | null;
  timeCritical?: boolean;
  /** The emergency is still open (null when unknown). */
  causeOpen?: boolean | null;
}

/** Mine, moved to someone else while I was pulled away. */
export interface CoverGiven {
  itemRef: CoverItemRef;
  causeId: string;
  /** Who has it now (a person), or the role it went to when nobody on the floor could take it. */
  ownerId: string | null;
  ownerRole: string | null;
  status: string;
  doneAt: string | null;
  /** The receiver took it. */
  accepted?: boolean;
  ownerName?: string | null;
  task?: string | null;
  residentName?: string | null;
  /** The emergency is still open (null when unknown); "Take back" once it is over. */
  causeOpen?: boolean | null;
}

/** GET /worklist/cover: building staff only. */
export interface CoverMine {
  real: true;
  received: CoverReceived[];
  given: CoverGiven[];
}

/** One emergency's live cover on the coverage read. */
export interface CoverageLive {
  causeId: string;
  /** "Covering for Jackie · Edna's emergency". */
  words: string;
  total: number;
  done: number;
  late: number;
  /** How many items each person (or role) holds, keyed by user id or role. */
  owners: Record<string, number>;
}

/** GET /facilities/:id/worklist/coverage. pct is null until anything has been covered. */
export interface FacilityCoverage {
  real: true;
  coveredOnTime: { covered: number; onTime: number; pct: number | null };
  live: CoverageLive[];
}

export const worklistCover = route<CoverMine>()('GET', `${A}/worklist/cover`, { honest: true });

export const worklistCoverAccept = route<{ real: true; accepted: number }, void, { itemRefs: CoverItemRef[] }>()(
  'POST', `${A}/worklist/cover/accept`, { honest: true });

export const worklistCoverPass = route<{ real: true; toUserId: string | null; toRole: string | null }, void, { itemRef: CoverItemRef }>()(
  'POST', `${A}/worklist/cover/pass`, { honest: true });

export const worklistCoverTakeBack = route<{ real: true; takenBack: number }, void, { causeId: string; itemRef?: CoverItemRef }>()(
  'POST', `${A}/worklist/cover/take-back`, { honest: true });

export const facilityCoverage = route<FacilityCoverage>()('GET', `${A}/facilities/:id/worklist/coverage`, { honest: true });

export const coverApi = { worklistCover, worklistCoverAccept, worklistCoverPass, worklistCoverTakeBack, facilityCoverage } as const;
